import { singleton } from "tsyringe";

import type { BadgeWithPopProps } from "@/shared/components/temparary/components/base/badge/badge-with-pop/BadgeWithPopModel";

export interface ArrowPosition {
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
}

// 각 단계별 인터페이스 정의
interface InitializedBuilder {
    calculateBasePosition(): PositionCalculatedBuilder;
}

interface PositionCalculatedBuilder {
    adjustForContainerBounds(containerWidth: number, containerLeft: number): BoundsAdjustedBuilder;
}

interface BoundsAdjustedBuilder {
    adjustForScreenBounds(windowHeight: number): ReadyToBuildBuilder;
}

interface ReadyToBuildBuilder {
    build(): {
        contentTop: number;
        contentLeft: number;
        arrowClass: string;
        arrowPosition: ArrowPosition;
    };
}

@singleton()
export class PopupPositionBuilder {
    private contentTop: number = 0;
    private contentLeft: number = 0;
    private arrowClass: string = "up-arrow";
    private arrowPosition: ArrowPosition = {};
    private arrowSize: number = 12;
    private margin: number = 16;
    private screenMargin: number = 16;

    private props!: BadgeWithPopProps;
    private badgeRect!: DOMRect;
    private popupRect!: DOMRect;

    // 시작 지점은 항상 init 메서드뿐
    public init(
        props: BadgeWithPopProps,
        badgeRect: DOMRect,
        popupRect: DOMRect,
    ): InitializedBuilder {
        this.contentTop = 0;
        this.contentLeft = 0;
        this.arrowClass = "up-arrow";
        this.arrowPosition = {};

        this.props = props;
        this.badgeRect = badgeRect;
        this.popupRect = popupRect;
        this.arrowSize = this.props.arrowSize || 12;
        this.margin = this.props.margin || 16;
        this.screenMargin = this.props.screenMargin || 16;

        // InitializedBuilder 인터페이스를 구현한 객체 반환
        return {
            calculateBasePosition: () => this.calculateBasePosition(),
        };
    }

    private calculateBasePosition(): PositionCalculatedBuilder {
        switch (this.props.position) {
            case "top":
                this.contentTop = this.badgeRect.top - (this.popupRect.height + this.margin);
                this.contentLeft =
                    this.badgeRect.left - (this.popupRect.width - this.badgeRect.width) / 2;
                this.arrowClass = "down-arrow";
                this.arrowPosition = {
                    left: `${(this.popupRect.width - this.arrowSize) / 2}px`,
                };
                break;
            case "left":
                this.contentTop =
                    this.badgeRect.top - (this.popupRect.height - this.badgeRect.height) / 2;
                this.contentLeft = this.badgeRect.left - (this.popupRect.width + this.margin);
                this.arrowClass = "right-arrow";
                break;
            case "right":
                this.contentTop =
                    this.badgeRect.top - (this.popupRect.height - this.badgeRect.height) / 2;
                this.contentLeft = this.badgeRect.right + this.margin;
                this.arrowClass = "left-arrow";
                break;
            default: // bottom
                this.contentTop = this.badgeRect.bottom + this.margin;
                this.contentLeft =
                    this.badgeRect.left - (this.popupRect.width - this.badgeRect.width) / 2;
                this.arrowClass = "up-arrow";
                this.arrowPosition = {
                    left: `${(this.popupRect.width - this.arrowSize) / 2}px`,
                };
        }
        // PositionCalculatedBuilder 인터페이스를 구현한 객체 반환
        return {
            adjustForContainerBounds: (containerWidth, containerLeft) =>
                this.adjustForContainerBounds(containerWidth, containerLeft),
        };
    }

    private adjustForContainerBounds(
        containerWidth: number,
        containerLeft: number,
    ): BoundsAdjustedBuilder {
        const rightEdge = containerLeft + containerWidth;

        if (this.contentLeft + this.popupRect.width > rightEdge - this.screenMargin) {
            this.contentLeft = rightEdge - this.popupRect.width - this.screenMargin;
            this.adjustArrowForHorizontalPosition();
        }

        if (this.contentLeft < containerLeft + this.screenMargin) {
            this.contentLeft = containerLeft + this.screenMargin;
            this.adjustArrowForHorizontalPosition();
        }

        // BoundsAdjustedBuilder 인터페이스를 구현한 객체 반환
        return {
            adjustForScreenBounds: (windowHeight) => this.adjustForScreenBounds(windowHeight),
        };
    }

    private adjustForScreenBounds(windowHeight: number): ReadyToBuildBuilder {
        if (this.contentTop + this.popupRect.height > windowHeight - this.screenMargin) {
            this.contentTop = this.badgeRect.top - (this.popupRect.height + this.margin);
            this.arrowClass = "down-arrow";
        }

        if (this.contentTop < this.screenMargin) {
            this.contentTop = this.screenMargin;
            this.arrowClass = "up-arrow";
        }

        return {
            build: () => this.build(),
        };
    }

    private adjustArrowForHorizontalPosition(): void {
        if (this.props.position === "bottom" || this.props.position === "top") {
            const centerOffset = this.badgeRect.left + this.badgeRect.width / 2 - this.contentLeft;
            this.arrowPosition = {
                left: `${Math.min(Math.max(centerOffset, this.arrowSize), this.popupRect.width - this.arrowSize)}px`,
            };
        }
    }

    private build(): {
        contentTop: number;
        contentLeft: number;
        arrowClass: string;
        arrowPosition: ArrowPosition;
    } {
        return {
            contentTop: this.contentTop,
            contentLeft: this.contentLeft,
            arrowClass: this.arrowClass,
            arrowPosition: this.arrowPosition,
        };
    }
}
